import {
  Watcher,
  ComputedGetter,
  ComputedSetter,
} from 'yox-type/src/type'

import {
  WatcherOptions,
} from 'yox-type/src/options'

import Observer from './Observer'

import execute from 'yox-common/src/function/execute'

import * as array from 'yox-common/src/util/array'
import * as object from 'yox-common/src/util/object'
import * as constant from 'yox-common/src/util/constant'

/**
 * 计算属性
 *
 * 可配置 cache、deps、get、set 等
 */
export default class Computed {

  static current?: Computed

  keypath: string

  value: any

  deps: string[]

  cache: boolean

  fixed: boolean

  context: any

  observer: Observer

  getter: ComputedGetter

  setter: ComputedSetter | void

  watcher: Watcher

  watcherOptions: WatcherOptions

  unique: Record<string, boolean>

  constructor(
    keypath: string,
    sync: boolean,
    cache: boolean,
    deps: string[],
    observer: Observer,
    getter: ComputedGetter,
    setter: ComputedSetter | void
  ) {

    const instance = this

    instance.keypath = keypath
    instance.cache = cache

    instance.deps = deps

    instance.context = observer.context
    instance.observer = observer
    instance.getter = getter
    instance.setter = setter

    instance.unique = {}

    instance.watcher = function ($0: any, $1: any, $2: string) {

      // 计算属性的依赖变了会走进这里

      const oldValue = instance.value,
      newValue = instance.get(constant.TRUE)

      if (newValue !== oldValue) {
        observer.diff(keypath, newValue, oldValue)
      }

    }

    instance.watcherOptions = {
      sync,
      watcher: instance.watcher
    }

    if (instance.fixed = !array.falsy(deps)) {
      array.each(
        deps,
        function (dep) {
          observer.watch(
            dep,
            instance.watcherOptions
          )
        }
      )
    }

  }

  /**
   * 读取计算属性的值
   *
   * @param force 是否强制刷新缓存
   */
  get(force?: boolean): any {

    const instance = this,

    { getter, context } = instance

    // 禁用缓存
    if (!instance.cache) {
      instance.value = execute(getter, context)
    }

    // 减少取值频率，尤其是处理复杂的计算规则
    else if (force || !object.has(instance, constant.RAW_VALUE)) {

      // 如果写死了依赖，则不需要收集依赖
      if (instance.fixed) {
        instance.value = execute(getter, context)
      }
      else {

        // 清空上次收集的依赖
        instance.unbind()

        // 开始收集新的依赖
        const lastComputed = Computed.current
        Computed.current = instance

        instance.value = execute(getter, context)

        // 绑定新的依赖
        instance.bind()

        Computed.current = lastComputed
      }

    }

    return instance.value
  }

  set(value: any): void {
    const { setter, context } = this
    if (setter) {
      setter.call(context, value)
    }
  }

  /**
   * 添加依赖
   *
   * 这里只是为了保证依赖唯一，最后由 bind() 实现绑定
   *
   * @param dep
   */
  add(dep: string): void {
    this.unique[dep] = constant.TRUE
  }

  /**
   * 绑定依赖
   */
  bind(): void {

    const { unique, deps, observer, watcherOptions } = this

    object.each(
      unique,
      function (_: any, dep: string) {
        array.push(deps, dep)
        observer.watch(
          dep,
          watcherOptions
        )
      }
    )

    // 用完重置
    // 方便下次收集依赖
    this.unique = {}

  }

  /**
   * 解绑依赖
   */
  unbind(): void {

    const { deps, observer, watcher } = this

    array.each(
      deps,
      function (dep) {
        observer.unwatch(dep, watcher)
      },
      constant.TRUE
    )

    deps.length = 0

  }

}